import React, { useEffect, useState } from 'react'
import ConfirmModal from './Confirm.modal'
import { Form, Table, Button } from 'react-bootstrap'
import AdminService from '../../services/admin.service'
import toast from 'react-hot-toast';

export interface IPage {
    name: string;
    path: string;
}

interface IProps {
    clientId: string
    setShow: () => void
}


export default function ViewClientModal({ clientId, setShow }: IProps) {
    const [clientData, setClientData] = useState<any>(null);
    const [pages, setPages] = useState<IPage[]>([]);
    const [selectedPages, setSelectedPages] = useState<IPage[]>([]);

    async function getClientDetails() {
        if (!clientId || clientId === "") {
            return;
        }

        await AdminService.getClientById(clientId)
            .then((res) => {
                if (res.status === 200) {
                    setClientData(res.data?.data?.client || null);
                } else {
                    setClientData(null);
                }
            })
            .catch((err) => {
                console.error("Error fetching client details:", err);
                setClientData(null);
            });
    }

    async function getClientPages() {
        await AdminService.getClientPages()
            .then((res) => {
                if (res.status === 200) {
                    setPages(res.data?.data?.pages || []);
                } else {
                    setPages([]);
                }
            })
            .catch((err) => {
                console.error("Error fetching client pages:", err);
                setPages([]);
            });
    }

    useEffect(() => {
        if (clientId || clientId !== "") {
            getClientDetails();
            getClientPages();
        }
    }, [clientId]);

    useEffect(() => {
        if (clientData?.allowedPages) {
            const pageObjects = clientData.allowedPages.map((page: any) => {
                if (typeof page === 'string') {
                    return { name: page, path: page };
                } else {
                    return { name: page.name, path: page.path };
                }
            });
            setSelectedPages(pageObjects);
        }
    }, [clientData, pages]);

    const handlePageToggle = (pageToToggle: IPage) => {
        setSelectedPages(prev => {
            const isSelected = prev.some(p => p.path === pageToToggle.path);
            if (isSelected) {
                return prev.filter(p => p.path !== pageToToggle.path);
            } else {
                return [...prev, { name: pageToToggle.name, path: pageToToggle.path }];
            }
        });
    };

    const handleSave = async () => {
        await AdminService.updateClient(clientId, { allowedPages: selectedPages })
            .then((res) => {
                if (res.status === 200) {
                    toast.success("Client updated successfully");
                    setShow();
                }
            })
            .catch((err) => {
                console.error("Error updating client:", err);
                toast.error("Failed to update client");
            });
    };

    const handleCancel = () => {
        if (clientData?.allowedPages) {
            const pageObjects = clientData.allowedPages.map((page: any) => {
                if (typeof page === 'string') {
                    const matchingPage = pages.find(p => p.path === page);
                    return matchingPage || { name: page, path: page };
                } else {
                    return { name: page.name, path: page.path };
                }
            });
            setSelectedPages(pageObjects);
        }
        setShow();
    };

    return (
        <>
            <ConfirmModal
                size={'lg'}
                titleText={"View Client Details"}
                bodyText={
                    <>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Client ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={clientData?.username || ""}
                                    readOnly
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Available Pages</Form.Label>
                                <Table striped bordered hover size="sm">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Path</th>
                                            <th>Access</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pages.map((page: IPage) => (
                                            <tr key={page.path}>
                                                <td>{page.name}</td>
                                                <td>{page.path}</td>
                                                <td>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={selectedPages.some(p => p.path === page.path)}
                                                        onChange={() => handlePageToggle(page)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Form.Group>
                        </Form>
                    </>
                }
                show={clientId !== ""}
                onHide={handleCancel}
                button={[
                    {
                        label: "Cancel",
                        action: handleCancel,
                        variant: "secondary",
                    },
                    {
                        label: "Save",
                        action: handleSave,
                        variant: "primary",
                    },
                ]}
            />
        </>
    )
}